<template>
  <BaseModal
    modal-id="coordinate-edit-modal"
    title="座標変換補正値設定"
    :model-value="visible"
    @cancel="handleCancel"
    @confirm="handleSubmit"
  >
    <!-- モーダルボディのコンテンツ -->
    <div class="modal-body-content">
      <!-- 緯度入力フィールド -->
      <BaseInput
        v-model="form.latitudeOffset"
        label="緯度補正値"
        type="number"
        step="0.000001"
        :decimal-places="6"
        placeholder="緯度補正値を入力"
        required
      />

      <!-- 経度入力フィールド -->
      <BaseInput
        v-model="form.longitudeOffset"
        label="経度補正値"
        type="number"
        step="0.000001"
        :decimal-places="6"
        placeholder="経度補正値を入力"
        required
      />
    </div>
  </BaseModal>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import BaseModal from '@/components/molecules/BaseModal.vue'
import BaseInput from '@/components/atoms/BaseInput.vue'

/**
 * CoordinateEditModal - 座標変換補正値設定モーダル（Organismsレベルコンポーネント）
 *
 * BaseModalを使用した座標変換補正値編集専用のモーダルコンポーネントです。
 *
 * 責務:
 * - 座標変換補正値の入力フォーム表示
 * - 緯度・経度補正値の入力値管理
 * - キャンセル時の元の値への復元
 * - 親コンポーネントへの保存イベント通知
 */

const props = defineProps({
  /**
   * 初期座標補正値
   * { latitudeOffset: number, longitudeOffset: number } 形式のオブジェクト
   */
  value: {
    type: Object,
    default: () => ({
      latitudeOffset: 0,
      longitudeOffset: 0
    })
  },
  /**
   * モーダルの表示状態（v-model用）
   */
  visible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['input', 'save'])

/**
 * フォームデータ
 */
const form = ref({
  latitudeOffset: Number(props.value?.latitudeOffset || 0),
  longitudeOffset: Number(props.value?.longitudeOffset || 0)
})

/**
 * 初期値（キャンセル時の復元用）
 */
const originalValues = ref({
  latitudeOffset: Number(props.value?.latitudeOffset || 0),
  longitudeOffset: Number(props.value?.longitudeOffset || 0)
})

/**
 * フォームデータを初期化
 */
const initializeForm = () => {
  const latValue = props.value?.latitudeOffset ?? 0
  const lngValue = props.value?.longitudeOffset ?? 0

  form.value.latitudeOffset = Number(latValue)
  form.value.longitudeOffset = Number(lngValue)
  originalValues.value.latitudeOffset = Number(latValue)
  originalValues.value.longitudeOffset = Number(lngValue)
}

/**
 * モーダルを表示します
 */
const showModal = () => {
  emit('input', true)
}

/**
 * モーダルを非表示にします
 */
const hideModal = () => {
  emit('input', false)
}

/**
 * キャンセル時の処理（元の値に戻す）
 */
const handleCancel = () => {
  form.value.latitudeOffset = originalValues.value.latitudeOffset
  form.value.longitudeOffset = originalValues.value.longitudeOffset
  emit('input', false)
}

/**
 * フォーム送信処理
 */
const handleSubmit = () => {
  const formData = {
    latitudeOffset: Number(form.value.latitudeOffset) || 0,
    longitudeOffset: Number(form.value.longitudeOffset) || 0
  }

  emit('save', formData)

  nextTick(() => {
    emit('input', false)
  })
}

// propsのvalueが変更された際にフォームデータと初期値を更新
watch(() => props.value, (newValue) => {
  if (newValue) {
    initializeForm()
  }
}, { deep: true, immediate: true })

defineExpose({
  showModal,
  hideModal
})
</script>

<style lang="scss" scoped>
.modal-body-content {
  padding: 0;
}
</style>
